import { zodResolver } from '@hookform/resolvers/zod';
import { TextField, CircularProgress, Button, Alert, Stack } from '@mui/material';
import { ResetPasswordRequestSchema, type ResetPasswordRequestPayload } from '@vy/api-definition';
import { VyIcon } from '@vy/ui-design-system';
import type { Username } from '@vy/types';
import { isError, isNotNullOrUndefined } from '@vy/utils';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useRequestPasswordReset } from '../../../../../hooks/requests/use-request-reset-password';
import { InfoBox } from '../../../users-components/InfoBox';
import { BackToLogin } from '../BackToLogin';

type RequestPasswordResetFormProps = {
  onNext: (inputEmail: Username) => void;
};

export const RequestPasswordResetForm: React.FC<RequestPasswordResetFormProps> = ({ onNext }) => {
  const methods = useForm<ResetPasswordRequestPayload>({
    resolver: zodResolver(ResetPasswordRequestSchema),
  });

  const {
    formState: { isSubmitSuccessful, isSubmitting, errors },
  } = methods;
  const requestPasswordResetMutation = useRequestPasswordReset();

  const handleSubmit = methods.handleSubmit(async ({ email }) => {
    await requestPasswordResetMutation.mutateAsync({ email }, { onSuccess: () => onNext(email) });
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit}>
        <Stack direction="column" spacing={4} sx={{ width: '456px' }}>
          <VyIcon icon="LogoBlue" className="text-transparent" />

          <InfoBox
            title="Trouble logging in?"
            description="Enter your email and we'll send you a link to get back into your account."
          />

          <TextField
            label="Email"
            placeholder="example@gmail.com"
            variant="filled"
            size="medium"
            fullWidth
            error={isNotNullOrUndefined(errors.email)}
            helperText={errors.email?.message}
            {...methods.register('email')}
          />

          <Button
            variant="contained"
            fullWidth
            type="submit"
            size="large"
            endIcon={!isSubmitting && <VyIcon icon="ArrowSmallRight" />}
          >
            {isSubmitting && !isSubmitSuccessful ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Send login link'
            )}
          </Button>

          {requestPasswordResetMutation.isError && (
            <Alert severity="error">
              {isError(requestPasswordResetMutation.error)
                ? requestPasswordResetMutation.error.message
                : 'Please ensure email is valid.'}
            </Alert>
          )}

          <BackToLogin />
        </Stack>
      </form>
    </FormProvider>
  );
};
